import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { defaultFeatureReduction } from './featureReductions';
import { getInvestmentPin } from '../components/pins';
import { getMgmtTypeVisual, mgmtTypePinLayerOutFields } from './mgmtType';

/** Default configuration for picture-marker pin symbols — base size, opacity, and vertical offset. */
export const pinSymbolDefaultConfig = {
  className: 'pin',
  type: 'picture-marker',
  width: 28 * 1.2 as string | number,
  height: 38 * 1.2 as string | number,
  opacity: 0.5,
  yoffset: '0px'
};


/**
 * Produces a pin-symbol configuration object for a cluster layer, switching pin URLs and sizes based
 * on the layer's `MgmtType` classification (`DirectOp`, `Lease`, `Contract`, or the gray `Unknown`
 * fallback) sourced from the shared `mgmtTypeVisuals` contract, so cluster symbols can never drift
 * from the boundary/pin renderers.
 *
 * @param layerName - Either the special `'potentialAcquisition'` layer name, or an `MgmtType` classification code (`DirectOp` | `Lease` | `Contract` | `Unknown`) used to look up the appropriate pin URL and size.
 * @returns An object with url, width, height, opacity, yoffset, and className properties for use as a cluster layer symbol.
 */
export const createClusterLayerSymbol = (layerName: string) => {
  let url = '';
  let pinConfig = { ...pinSymbolDefaultConfig };
  const newPinSize = {
    width: `${32 * 20}px`,
    height: `${38 * 20}px`
  };
  if (layerName === 'potentialAcquisition') {
    url = getInvestmentPin();
    pinConfig = {
      ...pinSymbolDefaultConfig,
      width: newPinSize.width,
      height: newPinSize.height,
    };
  } else {
    // MgmtType classification code path (DirectOp | Lease | Contract | Unknown).
    // getMgmtTypeVisual classifies any unrecognized/blank value to the gray
    // Unknown pin rather than falling through to a default valid category.
    url = getMgmtTypeVisual(layerName).getPin();
    pinConfig = {
      ...pinSymbolDefaultConfig,
      width: newPinSize.width,
      height: newPinSize.height,
    };
  }
  return {
    url: url,
    ...pinSymbolDefaultConfig,
    ...pinConfig
  };
};
/**
 * Returns a pin cluster layer configuration object with minScale 0, maxScale 1000000, and a name-based id/layerName for grouping pins.
 *
 * @param  - An object with a name property used for label and identifier generation.
 * @returns Cluster pin layer configuration with minScale, maxScale, id, layerName, outFields, and featureReduction properties."}
 */
export const clusterPinLayerConfig = ({ name }: { name: string }) => ({
  minScale: 0,
  // maxScale: 0,
  maxScale: 1000000,
  id: `pin-cluster: ${name}`,
  layerName: `pin-cluster: ${name}`,
  outFields: mgmtTypePinLayerOutFields
});

/**
* Creates an ArcGIS FeatureLayer with clustering enabled, using a pin symbol that varies by layer name
* (directly operated, leased, PRI, etc.) and the default feature reduction settings.
*
* @param name - Layer name used to determine the cluster pin symbol and generate a unique layer ID.
* @param featureConfig - Optional additional FeatureLayer configuration merged into the layer definition.
* @returns A configured FeatureLayer instance with clustering and pin symbology applied.
*/
export const createClusteredFeatureLayer = (name: string, featureConfig: any = {}) => {
  const featureLayer = new FeatureLayer({
    ...clusterPinLayerConfig({ name }),
    ...featureConfig
  } as any);
  // Clone the shared default feature-reduction config per layer so mutating
  // `.symbol` below never leaks across concurrently-created pin-cluster
  // layers (each MgmtType category layer must own its own reduction config).
  const featureReduction = { ...defaultFeatureReduction, labelingInfo: [...defaultFeatureReduction.labelingInfo ?? []] };
  featureReduction.symbol = createClusterLayerSymbol(name) as any;
  featureLayer.featureReduction = featureReduction;

  return featureLayer;
};
